import { useState, useEffect, useRef, useCallback } from 'react';
import { useApi, discoveryApiRef, fetchApiRef } from '@backstage/core-plugin-api';
import type { Entity } from '@backstage/catalog-model';

export type K6TestStatus = 'idle' | 'initializing' | 'running' | 'completed' | 'failed' | 'unknown';

export interface K6TestRun {
  name: string;
  status: K6TestStatus;
  startTime?: string;
  completionTime?: string;
}

interface UseK6TestStatusResult {
  testRuns: K6TestRun[];
  latestStatus: K6TestStatus;
  loading: boolean;
  error: Error | undefined;
  trigger: (opts: { vus: number; duration: string; targetPath: string }) => Promise<void>;
  kill: (runName: string) => Promise<void>;
  fetchRunReport: (runName: string) => Promise<string>;
  configScript: string | undefined;
  configLoading: boolean;
  refresh: () => void;
}

function mapPhaseToStatus(phase?: string): K6TestStatus {
  switch (phase?.toLowerCase()) {
    case 'initialization':
    case 'created':
      return 'initializing';
    case 'started':
    case 'running':
      return 'running';
    case 'finished':
    case 'completed':
      return 'completed';
    case 'error':
    case 'failed':
      return 'failed';
    default:
      return 'idle';
  }
}

export function useK6TestStatus(entity: Entity): UseK6TestStatusResult {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const [testRuns, setTestRuns] = useState<K6TestRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | undefined>();
  const [configScript, setConfigScript] = useState<string | undefined>();
  const [configLoading, setConfigLoading] = useState(false);
  const mountedRef = useRef(true);
  const intervalRef = useRef<ReturnType<typeof setInterval>>();

  const annotations = entity.metadata.annotations ?? {};
  const namespace = annotations['k6/test-namespace'] ?? 'demo-apps';
  const configMapName = annotations['k6/test-configmap'] ?? '';
  const componentName = entity.metadata.name;

  const fetchConfigMap = useCallback(async () => {
    if (!configMapName) return;
    setConfigLoading(true);
    try {
      const baseUrl = await discoveryApi.getBaseUrl('kubernetes');
      const resp = await fetchApi.fetch(
        `${baseUrl}/proxy/api/v1/namespaces/${encodeURIComponent(namespace)}/configmaps/${encodeURIComponent(configMapName)}`,
      );
      if (resp.ok) {
        const cm = await resp.json();
        const script = cm.data?.['load-test.js'] ?? undefined;
        if (mountedRef.current) setConfigScript(script);
      }
    } catch {
      // ConfigMap not accessible — degrade gracefully
    } finally {
      if (mountedRef.current) setConfigLoading(false);
    }
  }, [discoveryApi, fetchApi, namespace, configMapName]);

  const fetchTestRuns = useCallback(async () => {
    if (!configMapName) {
      setLoading(false);
      return;
    }
    try {
      const baseUrl = await discoveryApi.getBaseUrl('kubernetes');
      const labelSelector = encodeURIComponent(`app=${componentName},app.kubernetes.io/component=load-testing`);

      // List TestRun CRDs via the Kubernetes proxy
      let runs: K6TestRun[] = [];
      try {
        const trResp = await fetchApi.fetch(
          `${baseUrl}/proxy/apis/k6.io/v1alpha1/namespaces/${encodeURIComponent(namespace)}/testruns?labelSelector=${labelSelector}`,
        );
        if (trResp.ok) {
          const trData = await trResp.json();
          runs = (trData.items ?? []).map((item: any) => ({
            name: item.metadata?.name ?? 'unknown',
            status: mapPhaseToStatus(item.status?.stage),
            startTime: item.status?.conditions?.[0]?.lastTransitionTime ?? item.metadata?.creationTimestamp,
            completionTime: item.status?.conditions?.find((c: any) => c.type === 'TestRunCompleted')?.lastTransitionTime,
          }));
        }
      } catch {
        // TestRun CRDs might not be accessible — degrade gracefully
      }

      if (mountedRef.current) {
        setTestRuns(runs.sort((a: K6TestRun, b: K6TestRun) =>
          (b.startTime ?? '').localeCompare(a.startTime ?? ''),
        ));
        setError(undefined);

        // If a test is running, poll faster
        const hasRunning = runs.some((r: K6TestRun) => r.status === 'running' || r.status === 'initializing');
        clearInterval(intervalRef.current);
        intervalRef.current = setInterval(fetchTestRuns, hasRunning ? 3000 : 15000);
      }
    } catch (e) {
      if (mountedRef.current) {
        setError(e instanceof Error ? e : new Error(String(e)));
      }
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, [discoveryApi, fetchApi, namespace, componentName, configMapName]);

  const trigger = useCallback(
    async (opts: { vus: number; duration: string; targetPath: string }) => {
      const baseUrl = await discoveryApi.getBaseUrl('kubernetes');
      const targetService = annotations['k6/target-service'] ?? 'frontend';
      const targetPort = annotations['k6/target-port'] ?? '';
      const timestamp = Date.now();

      const testRunBody = {
        apiVersion: 'k6.io/v1alpha1',
        kind: 'TestRun',
        metadata: {
          name: `k6-${componentName}-${timestamp}`,
          namespace,
          labels: {
            app: componentName,
            'backstage.io/component': componentName,
            'app.kubernetes.io/managed-by': 'backstage',
            'app.kubernetes.io/component': 'load-testing',
          },
        },
        spec: {
          parallelism: 1,
          script: {
            configMap: {
              name: configMapName,
              file: 'load-test.js',
            },
          },
          runner: {
            image: 'grafana/k6:latest',
            envFrom: [{ configMapRef: { name: configMapName } }],
            env: [
              { name: 'K6_OTEL_SERVICE_NAME', value: `k6-${componentName}` },
              { name: 'TEST_VUS', value: String(opts.vus) },
              { name: 'TEST_DURATION', value: opts.duration },
              {
                name: 'TARGET_URL',
                value: `http://${targetService}.${namespace}.svc.cluster.local${targetPort ? `:${targetPort}` : ''}${opts.targetPath}`,
              },
            ],
          },
        },
      };

      const resp = await fetchApi.fetch(
        `${baseUrl}/proxy/apis/k6.io/v1alpha1/namespaces/${encodeURIComponent(namespace)}/testruns`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(testRunBody),
        },
      );

      if (!resp.ok) {
        const text = await resp.text();
        throw new Error(`Failed to create TestRun: ${resp.status} ${text}`);
      }

      // Refresh immediately
      await fetchTestRuns();
    },
    [discoveryApi, fetchApi, namespace, componentName, configMapName, annotations, fetchTestRuns],
  );

  const kill = useCallback(
    async (runName: string) => {
      const baseUrl = await discoveryApi.getBaseUrl('kubernetes');

      // 1. Delete the TestRun CRD
      const deleteResp = await fetchApi.fetch(
        `${baseUrl}/proxy/apis/k6.io/v1alpha1/namespaces/${encodeURIComponent(namespace)}/testruns/${encodeURIComponent(runName)}`,
        { method: 'DELETE', headers: { 'Content-Type': 'application/json' } },
      );
      if (!deleteResp.ok) {
        const text = await deleteResp.text();
        throw new Error(`Failed to delete TestRun: ${deleteResp.status} ${text}`);
      }

      // 2. Find and delete pods spawned by the load test
      const labelSelector = encodeURIComponent(
        `app=${componentName},app.kubernetes.io/component=load-testing`,
      );
      try {
        const podsResp = await fetchApi.fetch(
          `${baseUrl}/proxy/api/v1/namespaces/${encodeURIComponent(namespace)}/pods?labelSelector=${labelSelector}`,
        );
        if (podsResp.ok) {
          const podsData = await podsResp.json();
          const activePods = (podsData.items ?? []).filter((pod: any) => {
            const phase = pod.status?.phase;
            return phase === 'Running' || phase === 'Pending';
          });
          await Promise.all(
            activePods.map((pod: any) =>
              fetchApi.fetch(
                `${baseUrl}/proxy/api/v1/namespaces/${encodeURIComponent(namespace)}/pods/${encodeURIComponent(pod.metadata.name)}`,
                { method: 'DELETE', headers: { 'Content-Type': 'application/json' } },
              ),
            ),
          );
        }
      } catch {
        // Pod cleanup is best-effort
      }

      // Refresh immediately
      await fetchTestRuns();
    },
    [discoveryApi, fetchApi, namespace, componentName, fetchTestRuns],
  );

  const fetchRunReport = useCallback(
    async (runName: string): Promise<string> => {
      const baseUrl = await discoveryApi.getBaseUrl('kubernetes');
      const podsResp = await fetchApi.fetch(
        `${baseUrl}/proxy/api/v1/namespaces/${encodeURIComponent(namespace)}/pods`,
      );
      if (!podsResp.ok) {
        throw new Error(`Failed to list pods: ${podsResp.status}`);
      }
      const podsData = await podsResp.json();
      const pod = (podsData.items ?? []).find((p: any) => {
        const name = p.metadata?.name ?? '';
        return (
          name.startsWith(runName) &&
          !name.includes('-initializer-') &&
          !name.includes('-starter-')
        );
      });
      if (!pod) {
        throw new Error(`No pod found for run ${runName}`);
      }
      const logResp = await fetchApi.fetch(
        `${baseUrl}/proxy/api/v1/namespaces/${encodeURIComponent(namespace)}/pods/${encodeURIComponent(pod.metadata.name)}/log`,
      );
      if (!logResp.ok) {
        throw new Error(`Failed to fetch logs: ${logResp.status}`);
      }
      return logResp.text();
    },
    [discoveryApi, fetchApi, namespace],
  );

  useEffect(() => {
    mountedRef.current = true;
    fetchTestRuns();
    fetchConfigMap();
    intervalRef.current = setInterval(fetchTestRuns, 15000);
    return () => {
      mountedRef.current = false;
      clearInterval(intervalRef.current);
    };
  }, [fetchTestRuns, fetchConfigMap]);

  const latestStatus = testRuns[0]?.status ?? 'idle';

  return { testRuns: testRuns.slice(0, 10), latestStatus, loading, error, trigger, kill, fetchRunReport, configScript, configLoading, refresh: fetchTestRuns };
}
